import { useState, useEffect, useCallback } from 'react';
import { Activity, ChevronUp, ChevronDown, Wifi, Database, Bot } from 'lucide-react';
import { healthService, type HealthResponse, type ServiceStatus } from '../../services/health.service';

const STATUS_DOT: Record<ServiceStatus, string> = {
  ok:       'bg-emerald-400',
  degraded: 'bg-amber-400',
  offline:  'bg-red-400',
};

const STATUS_TEXT: Record<ServiceStatus, string> = {
  ok:       'Conectado',
  degraded: 'Degradado',
  offline:  'Offline',
};

const STATUS_TEXT_COLOR: Record<ServiceStatus, string> = {
  ok:       'text-emerald-600',
  degraded: 'text-amber-600',
  offline:  'text-red-600',
};

const POLL_INTERVAL_MS = 60_000;

interface Props {
  collapsed: boolean;
}

export default function HealthStatusWidget({ collapsed }: Props) {
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [expanded, setExpanded] = useState(false);

  const fetchHealth = useCallback(() => {
    healthService.getHealth()
      .then(setHealth)
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchHealth();
    const id = setInterval(() => { if (!document.hidden) fetchHealth(); }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetchHealth]);

  if (!health) return null;

  const services = [
    { key: 'whatsapp', label: 'WhatsApp', icon: Wifi,     data: health.whatsapp },
    { key: 'siga',     label: 'SIGA',     icon: Database,  data: health.siga },
    { key: 'agent',    label: 'Agente',   icon: Bot,       data: health.agent },
  ] as const;

  const hasIssue = services.some(s => s.data.status !== 'ok');

  if (collapsed) {
    return (
      <div className="flex justify-center py-3 border-t border-slate-200 dark:border-white/[0.06]">
        <div
          title={hasIssue ? 'Há problemas de conexão' : 'Todos os serviços OK'}
          className={`size-2.5 rounded-full ${hasIssue ? 'bg-amber-400' : 'bg-emerald-400'} animate-pulse`}
        />
      </div>
    );
  }

  return (
    <div className="border-t border-slate-200 dark:border-white/[0.06] mt-auto">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-slate-50 dark:hover:bg-white/[0.06] transition-colors"
      >
        <div className="flex items-center gap-2">
          <Activity className="size-3.5 text-slate-400 dark:text-slate-500" />
          <span className="text-xs font-medium text-slate-500 dark:text-slate-400">Status</span>
          {hasIssue && (
            <span className="size-1.5 rounded-full bg-amber-400 animate-pulse" />
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {services.map(s => (
            <span
              key={s.key}
              title={`${s.label}: ${STATUS_TEXT[s.data.status]}`}
              className={`size-1.5 rounded-full ${STATUS_DOT[s.data.status]}`}
            />
          ))}
          {expanded
            ? <ChevronDown className="size-3 text-slate-400 dark:text-slate-500 ml-1" />
            : <ChevronUp className="size-3 text-slate-400 dark:text-slate-500 ml-1" />
          }
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-3 space-y-2">
          {services.map(({ key, label, icon: Icon, data }) => (
            <div key={key} className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Icon className="size-3.5 text-slate-400 dark:text-slate-500" />
                <span className="text-xs text-slate-600 dark:text-slate-400">{label}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className={`text-[11px] font-medium ${STATUS_TEXT_COLOR[data.status]}`}>
                  {STATUS_TEXT[data.status]}
                </span>
                <span className={`size-1.5 rounded-full ${STATUS_DOT[data.status]}`} />
              </div>
            </div>
          ))}
          {health.agent.sessions_today !== undefined && (
            <p className="text-[10px] text-slate-400 dark:text-slate-500 pt-1">
              {health.agent.sessions_today} sessões nas últimas 24h
            </p>
          )}
        </div>
      )}
    </div>
  );
}
